import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useQuery } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'
import { toast } from 'components/global/CUK/Toast/Toast'
import { trackEvent } from 'analytics'

import { OPERATION_FIND_RAN_ROOM } from 'pages/Ran/operations-gql/queries'
import validationSchema from 'pages/Ran/Form/validation'
import TextField from 'components/global/CUK/TextField'
import { FormLabel } from 'pages/Ran/Form/FormStyles'
import Modal, { Title, IconTitle } from '../components/Modal'
import useActions from 'pages/Ran/Actions/use-actions'
import Loading from 'components/global/Loading'
import StimeFields, { buildPreSharedKeys, getStimeDefaultValues, useStimeContext } from 'pages/Ran/Form/StimeFields'
import { Box, Button, Stack, Typography, useTheme } from '@mui/material'

const Form = ({ room, processing, isEditing = false, onSubmit, onClose }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const stimeContext = useStimeContext()
  const withStimeFields = stimeContext.isVisible && stimeContext.isEditable
  const stimeDefaultValues = getStimeDefaultValues(room, stimeContext)
  const isSsidLocked = isEditing && Boolean(stimeDefaultValues.ssid_id)
  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: yupResolver(validationSchema(t, { withStimeFields })),
    defaultValues: {
      ...room,
      ...stimeDefaultValues
    }
  })
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = methods

  const handleValidSubmit = values =>
    onSubmit({
      ...values,
      preSharedKeys: withStimeFields ? buildPreSharedKeys(values) : undefined
    })

  return (
    <FormProvider {...methods}>
      <form
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          width: '100%'
        }}>
        <input type="hidden" name="id" value={room.id} />
        {stimeContext.isVisible && (
          <StimeFields
            ssids={stimeContext.ssids}
            getVlanOptions={stimeContext.getVlanOptions}
            disabled={!stimeContext.isEditable}
            isSsidLocked={isSsidLocked}
          />
        )}
        <Box>
          <FormLabel>{t('ran.form.name')}</FormLabel>
          <Controller
            control={control}
            name="name"
            render={({ field: { value, onChange, ref } }) => (
              <TextField
                value={value}
                setvalue={onChange}
                fullWidth
                input={{
                  inputRef: ref
                }}
                footer={{
                  errorLabel: errors.name?.message
                }}
                error={Boolean(errors.name)}
              />
            )}
          />
        </Box>
        <Box>
          <FormLabel>{t('ran.form.description')}</FormLabel>
          <Controller
            control={control}
            name="description"
            render={({ field: { value, onChange, ref } }) => (
              <Stack spacing={1}>
                <TextField
                  value={value}
                  setvalue={text => {
                    if (text.length <= 250) {
                      onChange(text)
                    }
                  }}
                  fullWidth
                  input={{
                    multiline: true,
                    minRows: 3,
                    maxRows: 10,
                    inputRef: ref
                  }}
                  footer={{
                    errorLabel: errors.description?.message
                  }}
                  error={Boolean(errors.description)}
                />
                <Typography
                  sx={{
                    color: theme.palette.grey['800'],
                    fontSize: '12px',
                    fontWeight: 500
                  }}>
                  {value?.length || 0} / 250
                </Typography>
              </Stack>
            )}
          />
        </Box>
        <Stack direction={'row'} justifyContent={'space-between'} gap={5}>
          <Button size={'small'} variant={'outlined'} onClick={onClose}>
            {t('ui_elements.cancel')}
          </Button>
          <Button
            disabled={processing}
            size={'small'}
            variant={'contained'}
            color={'primary'}
            onClick={handleSubmit(handleValidSubmit)}>
            {processing ? t('ui_elements.in_progress') : t('ui_elements.form_validate')}
          </Button>
        </Stack>
      </form>
    </FormProvider>
  )
}

const FormCreate = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { create, processing } = useActions()
  const handleClose = () => {
    navigate('..')
  }
  const handleSubmit = async ({ name, description, preSharedKeys }) => {
    const { error } = await create({ name, description: description || '', preSharedKeys })
    if (error) {
      toast.error(t('ran.create.toast_error'))
    } else {
      toast.success(t('ran.create.toast_success'))
      trackEvent('private_wifi_created', {
        action_scope: 'item',
        has_description: Boolean(description?.trim())
      })
    }
    navigate('..')
  }

  return (
    <Modal onClose={handleClose}>
      <IconTitle variant={'primary'} icon={'pen'} />
      <Title>{t('ran.create.title')}</Title>
      <Form
        processing={processing}
        room={{ name: '', description: '' }}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />
    </Modal>
  )
}

export const FormUpdate = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { update, processing } = useActions()
  const { roomId, siteId } = useParams()
  const { data, loading } = useQuery(OPERATION_FIND_RAN_ROOM, {
    variables: {
      sf_id: siteId,
      room_id: parseInt(roomId)
    }
  })

  const handleSubmit = async ({ id, name, description, preSharedKeys }) => {
    const { error } = await update({ id, name, description: description || '', preSharedKeys })
    if (error) {
      toast.error(t('ran.update.toast_error'))
    } else {
      toast.success(t('ran.update.toast_success'))
      trackEvent('private_wifi_updated', {
        action_scope: 'item',
        has_description: Boolean(description?.trim())
      })
    }
    navigate('..')
  }

  const handleClose = () => {
    navigate('..')
  }

  if (loading) {
    return <Loading />
  }

  return (
    <Modal onClose={handleClose}>
      <IconTitle variant={'primary'} icon={'pen'} />
      <Title>{t('ran.update.title')}</Title>
      {loading ? (
        <Loading />
      ) : (
        <Form processing={processing} room={data.room} isEditing onClose={handleClose} onSubmit={handleSubmit} />
      )}
    </Modal>
  )
}

export default FormCreate
